/**
 * 军队面板组件
 * 显示军队管理和调度
 */

import { useState } from 'react'
import { useMilitaryStore } from '../../stores/militaryStore'
import './MilitaryPanel.css'

interface MilitaryPanelProps {
  factionId: string
}

export function MilitaryPanel({ factionId }: MilitaryPanelProps) {
  const [activeTab, setActiveTab] = useState<'armies' | 'battles' | 'facilities' | 'technologies' | 'recruitment' | 'orders'>('armies')
  const [selectedArmy, setSelectedArmy] = useState<string | null>(null)
  const [selectedBattle, setSelectedBattle] = useState<string | null>(null)
  
  const {
    armies,
    battles,
    facilities,
    recruitmentPlans,
    orders,
    createArmy,
    addUnitToArmy,
    startBattle,
    buildFacility,
    createRecruitmentPlan,
    issueOrder
  } = useMilitaryStore()
  
  // 过滤当前势力的军队
  const factionArmies = Array.from(armies.values()).filter(army => army.factionId === factionId)
  
  // 过滤当前势力的军事设施
  const factionFacilities = Array.from(facilities.values()).filter(facility => facility.factionId === factionId)
  
  // 过滤当前势力的征兵计划
  const factionRecruitmentPlans = Array.from(recruitmentPlans.values()).filter(plan => plan.factionId === factionId)
  
  // 过滤当前势力的军事指令
  const factionOrders = Array.from(orders.values()).filter(order => {
    const army = armies.get(order.armyId)
    return army?.factionId === factionId
  })
  
  // 处理创建军队
  const handleCreateArmy = () => {
    const armyId = createArmy('周武王亲军', factionId, 'char_zhou_wuwang', 'city_gaojing')
    addUnitToArmy(armyId, 'heavy_infantry', 5000)
    addUnitToArmy(armyId, 'chariots', 1000)
    addUnitToArmy(armyId, 'archers', 3000)
  }
  
  // 处理开始战斗
  const handleStartBattle = () => {
    if (factionArmies.length >= 2) {
      startBattle('牧野之战', '牧野', [factionArmies[0].id], [factionArmies[1].id])
    }
  }
  
  // 处理建造军事设施
  const handleBuildFacility = () => {
    buildFacility('barracks', '镐京兵营', 'city_gaojing', factionId)
  }
  
  // 处理创建征兵计划
  const handleCreateRecruitmentPlan = () => {
    createRecruitmentPlan(factionId, 'city_gaojing', [
      { type: 'heavy_infantry', count: 2000 },
      { type: 'light_cavalry', count: 1000 }
    ])
  }
  
  // 处理发布军事指令
  const handleIssueOrder = () => {
    if (factionArmies.length > 0) {
      issueOrder('move', factionArmies[0].id, 'city_luoyang')
    }
  }
  
  return (
    <div className="military-panel">
      {/* 标签页 */}
      <div className="military-tabs">
        <button 
          className={`military-tab ${activeTab === 'armies' ? 'active' : ''}`}
          onClick={() => setActiveTab('armies')}
        >
          军队
        </button>
        <button 
          className={`military-tab ${activeTab === 'battles' ? 'active' : ''}`}
          onClick={() => setActiveTab('battles')}
        >
          战斗
        </button>
        <button 
          className={`military-tab ${activeTab === 'facilities' ? 'active' : ''}`}
          onClick={() => setActiveTab('facilities')}
        >
          设施
        </button>
        <button 
          className={`military-tab ${activeTab === 'technologies' ? 'active' : ''}`}
          onClick={() => setActiveTab('technologies')}
        >
          科技
        </button>
        <button 
          className={`military-tab ${activeTab === 'recruitment' ? 'active' : ''}`}
          onClick={() => setActiveTab('recruitment')}
        >
          征兵
        </button>
        <button 
          className={`military-tab ${activeTab === 'orders' ? 'active' : ''}`}
          onClick={() => setActiveTab('orders')}
        >
          指令
        </button>
      </div>
      
      {/* 内容区域 */}
      <div className="military-content">
        {/* 军队管理 */}
        {activeTab === 'armies' && (
          <div className="armies-section">
            <div className="section-header">
              <h3>军队管理</h3>
              <button 
                className="create-army-btn"
                onClick={handleCreateArmy}
              >
                创建军队
              </button>
            </div>
            
            <div className="armies-list">
              {factionArmies.map(army => (
                <div 
                  key={army.id}
                  className={`army-card ${army.status}`}
                  onClick={() => setSelectedArmy(army.id)}
                >
                  <div className="army-header">
                    <h4>{army.name}</h4>
                    <span className={`army-status ${army.status}`}>
                      {getArmyStatusText(army.status)}
                    </span>
                  </div>
                  <div className="army-details">
                    <span>指挥官: {army.commanderId}</span>
                    <span>位置: {army.currentLocation}</span>
                    <span>兵力: {army.totalStrength}</span>
                    <span>士气: {army.morale}</span>
                  </div>
                  <div className="army-units">
                    <h5>兵力组成:</h5>
                    <div className="units-grid">
                      {army.units.map(unit => (
                        <div key={unit.type} className="unit-item">
                          <span className="unit-name">{unit.name}</span>
                          <span className="unit-count">{unit.count}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              ))}
              
              {factionArmies.length === 0 && (
                <div className="empty-state">
                  <span className="empty-icon">⚔️</span>
                  <p>暂无军队</p>
                  <button 
                    className="create-army-btn small"
                    onClick={handleCreateArmy}
                  >
                    创建第一支军队
                  </button>
                </div>
              )}
            </div>
          </div>
        )}
        
        {/* 战斗管理 */}
        {activeTab === 'battles' && (
          <div className="battles-section">
            <div className="section-header">
              <h3>战斗管理</h3>
              <button 
                className="start-battle-btn"
                onClick={handleStartBattle}
              >
                开始战斗
              </button>
            </div>
            
            <div className="battles-list">
              {Array.from(battles.values()).map(battle => (
                <div 
                  key={battle.id}
                  className={`battle-card ${battle.status}`}
                  onClick={() => setSelectedBattle(battle.id)}
                >
                  <div className="battle-header">
                    <h4>{battle.name}</h4>
                    <span className={`battle-status ${battle.status}`}>
                      {getBattleStatusText(battle.status)}
                    </span>
                  </div>
                  <div className="battle-details">
                    <span>地点: {battle.location}</span>
                    <span>开始时间: {formatDate(battle.startedAt)}</span>
                    {battle.endedAt && (
                      <span>结束时间: {formatDate(battle.endedAt)}</span>
                    )}
                  </div>
                  <div className="battle-forces">
                    <div className="forces-side">
                      <h5>进攻方:</h5>
                      {battle.attackers.map(attacker => (
                        <div key={attacker.armyId} className="force-item">
                          <span>{attacker.factionId}</span>
                          <span>{attacker.strength} 人</span>
                        </div>
                      ))}
                    </div>
                    <div className="forces-side">
                      <h5>防守方:</h5>
                      {battle.defenders.map(defender => (
                        <div key={defender.armyId} className="force-item">
                          <span>{defender.factionId}</span>
                          <span>{defender.strength} 人</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              ))}
              
              {battles.size === 0 && (
                <div className="empty-state">
                  <span className="empty-icon">⚔️</span>
                  <p>暂无战斗记录</p>
                </div>
              )}
            </div>
          </div>
        )}
        
        {/* 军事设施 */}
        {activeTab === 'facilities' && (
          <div className="facilities-section">
            <div className="section-header">
              <h3>军事设施</h3>
              <button 
                className="build-facility-btn"
                onClick={handleBuildFacility}
              >
                建造设施
              </button>
            </div>
            
            <div className="facilities-list">
              {factionFacilities.map(facility => (
                <div key={facility.id} className="facility-card">
                  <div className="facility-header">
                    <h4>{facility.name}</h4>
                    <span className="facility-level">Lv.{facility.level}</span>
                  </div>
                  <div className="facility-details">
                    <span>类型: {getFacilityTypeText(facility.type)}</span>
                    <span>城市: {facility.cityId}</span>
                    <span>状态: {facility.status === 'operating' ? '运行中' : '升级中'}</span>
                  </div>
                  <div className="facility-effects">
                    <h5>效果:</h5>
                    <div className="effects-grid">
                      <div className="effect-item">
                        <span>训练速度: +{facility.effects.trainingSpeed}%</span>
                      </div>
                      <div className="effect-item">
                        <span>单位质量: +{facility.effects.unitQuality}%</span>
                      </div>
                      <div className="effect-item">
                        <span>招募速度: +{facility.effects.recruitmentSpeed}%</span>
                      </div>
                      <div className="effect-item">
                        <span>装备生产: +{facility.effects.equipmentProduction}%</span>
                      </div>
                    </div>
                  </div>
                  <div className="facility-actions">
                    {facility.level < 5 && (
                      <button className="upgrade-btn">升级</button>
                    )}
                  </div>
                </div>
              ))}
              
              {factionFacilities.length === 0 && (
                <div className="empty-state">
                  <span className="empty-icon">🏰</span>
                  <p>暂无军事设施</p>
                  <button 
                    className="build-facility-btn small"
                    onClick={handleBuildFacility}
                  >
                    建造第一个军事设施
                  </button>
                </div>
              )}
            </div>
          </div>
        )}
        
        {/* 军事科技 */}
        {activeTab === 'technologies' && (
          <div className="technologies-section">
            <div className="section-header">
              <h3>军事科技</h3>
            </div>
            
            <div className="technologies-list">
              {/* 这里可以添加科技树 */}
              <div className="empty-state">
                <span className="empty-icon">🔬</span>
                <p>军事科技系统开发中</p>
              </div>
            </div>
          </div>
        )}
        
        {/* 征兵计划 */}
        {activeTab === 'recruitment' && (
          <div className="recruitment-section">
            <div className="section-header">
              <h3>征兵计划</h3>
              <button 
                className="create-recruitment-btn"
                onClick={handleCreateRecruitmentPlan}
              >
                制定征兵计划
              </button>
            </div>
            
            <div className="recruitment-list">
              {factionRecruitmentPlans.map(plan => (
                <div key={plan.id} className={`recruitment-card ${plan.status}`}>
                  <div className="recruitment-header">
                    <h4>征兵计划 #{plan.id.slice(-4)}</h4>
                    <span className={`recruitment-status ${plan.status}`}>
                      {getRecruitmentStatusText(plan.status)}
                    </span>
                  </div>
                  <div className="recruitment-details">
                    <span>城市: {plan.cityId}</span>
                    <span>预计完成: {formatDate(plan.estimatedCompletion)}</span>
                  </div>
                  <div className="recruitment-units">
                    <h5>征兵目标:</h5>
                    {plan.units.map(unit => (
                      <div key={unit.type} className="recruitment-unit">
                        <span>{getUnitTypeText(unit.type)}</span>
                        <span>{unit.count} 人</span>
                        <span>训练 {unit.trainingTime} 天</span>
                      </div>
                    ))}
                  </div>
                  <div className="recruitment-costs">
                    <h5>消耗:</h5>
                    <span>金钱: {plan.costs.money}</span>
                    <span>粮食: {plan.costs.food}</span>
                    <span>金属: {plan.costs.resources.metal || 0}</span>
                    <span>木材: {plan.costs.resources.wood || 0}</span>
                  </div>
                </div>
              ))}
              
              {factionRecruitmentPlans.length === 0 && (
                <div className="empty-state">
                  <span className="empty-icon">👥</span>
                  <p>暂无征兵计划</p>
                  <button 
                    className="create-recruitment-btn small"
                    onClick={handleCreateRecruitmentPlan}
                  >
                    制定第一个征兵计划
                  </button>
                </div>
              )}
            </div>
          </div>
        )}
        
        {/* 军事指令 */}
        {activeTab === 'orders' && (
          <div className="orders-section">
            <div className="section-header">
              <h3>军事指令</h3>
              <button 
                className="issue-order-btn"
                onClick={handleIssueOrder}
              >
                发布指令
              </button>
            </div>
            
            <div className="orders-list">
              {factionOrders.map(order => {
                const army = armies.get(order.armyId)
                return (
                  <div key={order.id} className={`order-card ${order.status}`}>
                    <div className="order-header">
                      <h4>{getOrderTypeText(order.type)}</h4>
                      <span className={`order-status ${order.status}`}>
                        {getOrderStatusText(order.status)}
                      </span>
                    </div>
                    <div className="order-details">
                      <span>军队: {army?.name || order.armyId}</span>
                      <span>目标: {order.target}</span>
                      <span>发布时间: {formatDate(order.issuedAt)}</span>
                    </div>
                  </div>
                )
              })}
              
              {factionOrders.length === 0 && (
                <div className="empty-state">
                  <span className="empty-icon">📜</span>
                  <p>暂无军事指令</p>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
      
      {/* 选中军队详情 */}
      {selectedArmy && (
        <div className="army-detail-modal">
          <div className="modal-content">
            <div className="modal-header">
              <h3>军队详情</h3>
              <button 
                className="close-btn"
                onClick={() => setSelectedArmy(null)}
              >
                ×
              </button>
            </div>
            <div className="modal-body">
              {/* 详细信息 */}
            </div>
          </div>
        </div>
      )}
      
      {/* 选中战斗详情 */}
      {selectedBattle && (
        <div className="battle-detail-modal">
          <div className="modal-content">
            <div className="modal-header">
              <h3>战斗详情</h3>
              <button 
                className="close-btn"
                onClick={() => setSelectedBattle(null)}
              >
                ×
              </button>
            </div>
            <div className="modal-body">
              {/* 详细信息 */}
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

// 辅助函数
function getArmyStatusText(status: string): string {
  const statusTexts = {
    stationed: '驻守',
    marching: '行军中',
    battling: '战斗中',
    besieging: '围城',
    retreating: '撤退',
    disbanded: '解散'
  }
  return statusTexts[status as keyof typeof statusTexts] || '未知状态'
}

function getBattleStatusText(status: string): string {
  const statusTexts = {
    preparing: '准备中',
    fighting: '战斗中',
    victory: '胜利',
    defeat: '失败',
    draw: '平局',
    retreat: '撤退'
  }
  return statusTexts[status as keyof typeof statusTexts] || '未知状态'
}

function getFacilityTypeText(type: string): string {
  const typeTexts = {
    barracks: '兵营',
    training_ground: '训练场',
    armory: '军械库',
    stables: '马厩',
    siege_workshop: '攻城工坊',
    naval_base: '海军基地'
  }
  return typeTexts[type as keyof typeof typeTexts] || '未知类型'
}

function getUnitTypeText(type: string): string {
  const typeTexts = {
    heavy_infantry: '重步兵',
    light_infantry: '轻步兵',
    heavy_cavalry: '重骑兵',
    light_cavalry: '轻骑兵',
    archers: '弓箭手',
    crossbowmen: '弩手',
    chariots: '战车',
    siege_weapons: '攻城器械',
    navy: '水军'
  }
  return typeTexts[type as keyof typeof typeTexts] || '未知兵种'
}

function getRecruitmentStatusText(status: string): string {
  const statusTexts = {
    planning: '规划中',
    recruiting: '招募中',
    training: '训练中',
    completed: '已完成',
    cancelled: '已取消'
  }
  return statusTexts[status as keyof typeof statusTexts] || '未知状态'
}

function getOrderTypeText(type: string): string {
  const typeTexts = {
    move: '移动',
    attack: '攻击',
    defend: '防守',
    siege: '围城',
    retreat: '撤退',
    patrol: '巡逻'
  }
  return typeTexts[type as keyof typeof typeTexts] || '未知指令'
}

function getOrderStatusText(status: string): string {
  const statusTexts = {
    issued: '已发布',
    executing: '执行中',
    completed: '已完成',
    failed: '失败',
    cancelled: '已取消'
  }
  return statusTexts[status as keyof typeof statusTexts] || '未知状态'
}

function formatDate(date: { year: number; month: number; day: number }): string {
  return `${date.year}年${date.month}月${date.day}日`
}
